import { Injectable } from '@angular/core';
import { UserStateService } from '../user-state/user-state.service';
import { UserRole } from '../../../../../core/enums/users/role';
import { HttpClient } from '@angular/common/http';
import { AuthenticatedUser } from '../../../../../core/models/authenticated-user';
import { first, Observable, tap } from 'rxjs';
import { RouteConstants } from '../../../../../core/constants/routes';
import { Router } from '@angular/router';
import { MasqueradeRequest } from './models/masquerade-request';
import { AccessConstants } from '../../../../../core/constants/access-constants';
import { ApiRouteConstants } from '../../../../../core/constants/api-routes';

@Injectable({
    providedIn: 'root',
})
export class MasqueradeService {
    constructor(
        private readonly userStateService: UserStateService,
        private readonly http: HttpClient,
        private readonly router: Router,
    ) {}

    masqueradeAsUser(masqueradeRequest: MasqueradeRequest) {
        const request = this.http.post<AuthenticatedUser>(
            ApiRouteConstants.Masquerade.rootUrl,
            masqueradeRequest,
        );

        this.handleMasqueradeRequest(request);
    }

    stopMasquerading(): void {
        const dashboardUrl = `/${RouteConstants.Dashboard.getRootUrl()}`;

        // We need to perform navigation BEFORE stopping masquerading. We need the masquerade cancellation to go through
        // or BlockNavigationIfChangesGuard to ensure the user sees the "unsaved changes" dialog when a form is dirty and unsaved.
        this.router.navigate([dashboardUrl]).then((navigationSuccess) => {
            // navigationSuccess will be false when we are already on the dashboard since no navigation
            // occurs. As a result, we also need to call the stop masquerade logic on the dashboard specifically.
            if (navigationSuccess || this.router.url === dashboardUrl) {
                this.performStopMasquerading();
            } else {
                console.error('Navigation to dashboard failed.');
            }
        });
    }

    private performStopMasquerading() {
        // We need to remove the current user$ state to ensure the masquerade header is removed BEFORE navigation.
        // Otherwise, you'll see the masquerade header for a second or so after navigating to the dashboard.
        this.userStateService.user$.next(null);
        const request = this.http.delete<AuthenticatedUser>(
            ApiRouteConstants.Masquerade.rootUrl,
        );
        this.handleMasqueradeRequest(request);
    }

    shouldShowMasqueradeButton(userBeingMasqueradedRole: UserRole): boolean {
        const loggedInUserRole: UserRole = this.userStateService.getRole()!;

        const loggedInUserCanMasqueradeAsRoles =
            this.masqueradePermissions[loggedInUserRole] || [];
        return loggedInUserCanMasqueradeAsRoles.includes(
            userBeingMasqueradedRole,
        );
    }

    /**
     * Handles masquerade requests by setting the user and always navigating them to the dashboard.
     * @param request Start/stop request.
     */
    private handleMasqueradeRequest(request: Observable<AuthenticatedUser>) {
        request
            .pipe(
                tap((user) => {
                    this.userStateService.user$.next(user);
                }),
                first(),
            )
            .subscribe(() => {
                this.router.navigate([
                    `/${RouteConstants.Dashboard.getRootUrl()}`,
                ]);
            });
    }

    private readonly masqueradePermissions: Record<UserRole, UserRole[]> = {
        [UserRole.Admin]: AccessConstants.getAllUserRoles(),
        [UserRole.Customer]: [],
        [UserRole.Accounting]: [UserRole.Customer],
        [UserRole.CrmManager]: [UserRole.Customer],
        [UserRole.Advisor]: [
            UserRole.Customer,
            UserRole.CrmManager,
            UserRole.Accounting,
            UserRole.Advisor,
        ],
    };
}
